import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";
import {
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  CircularProgress,
  Typography,
  IconButton,
  Alert,
  dialogTitleClasses,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import { Form } from "react-final-form";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { createInterestApplication } from "../../api/client";

/**
 * PM5 T1 (chrome, batch "dialogs-modals") — converted from MUI v4 +
 * `makeStyles` to MUI v5+ (`@mui/material`) + `styled`/`sx`.
 * See docs/mui-conversion-guide.md; acceptance bar is visual parity.
 *
 * Three v4→v9 library-default changes in the Dialog family are compensated
 * for below (each marked PARITY RESTORE) — they are documented as new rows in
 * the guide's §2 table under (T1/dialogs-modals).
 */

/* ---------------------------------------------------------------------- *
 * styled() — bare host elements (no `sx` prop)
 * ---------------------------------------------------------------------- */

// was: useStyles().form — a bare <div>, so `styled()` rather than `sx`
// (guide §1.2 row 2).
const FormRoot = styled("div")({
  width: "100%", // Fix IE 11 issue.
});

/* ---------------------------------------------------------------------- *
 * sx — styles on MUI components
 * ---------------------------------------------------------------------- */

// was: useStyles().phoneInput — handed to `react-phone-input-2`'s `inputClass`,
// which only accepts a bare class-name string, so the rule keeps a stable
// className and is declared from the field-set wrapper below. Same pattern as
// `common/TwoStepPhoneInput.tsx` (guide §1.2b).
const PHONE_INPUT_CLASS = "DoorsClosedNotifyMe-phoneInput";

// was: useStyles().root — applied to the DialogTitle. v4's DialogTitle root was
// `{ margin: 0, padding: "16px 24px", flex: "0 0 auto" }` and this rule flattened
// the padding to 16px all round; v9's root is the same bar `margin` (which comes
// from Typography's own root instead), so the rule ports unchanged.
//
// API delta with no visual effect: v4's `disableTypography` prop is gone in v5+.
// v4 rendered `<div>{children}</div>`; v9 always renders
// `styled(Typography)` as `<h2 variant="h6">`. The only child here is the
// absolutely-positioned close button, so there is never an in-flow line box and
// the h6 metrics can't change the 32px box height — the change is DOM/semantic
// only (div → h2).
const dialogTitleSx: SxProps<Theme> = {
  margin: 0,
  padding: 2,
};

// was: useStyles().closeButton
const closeButtonSx: SxProps<Theme> = (theme) => ({
  position: "absolute",
  // `top`/`right` are NOT spacing-transformed by `sx` (their defaultSxConfig
  // entries are empty), so `1` would emit `1px` rather than v4's
  // `theme.spacing(1)` = 8px. Read the value off the theme instead.
  right: theme.spacing(1),
  top: theme.spacing(1),
  color: theme.palette.grey[500],
  // PARITY RESTORE — v4's IconButton root padding was 12; v9's default
  // (`medium`) is 8 and this rule sets no padding of its own (guide §2). Pinned
  // rather than `size="large"`, which would also push the icon glyph from
  // 24px to 28px.
  padding: "12px",
});

// was: useStyles().dialogIcon — `fill` is not an `sx` style prop, so a palette
// path string would be emitted raw; read it off the theme (guide §1.2 row 4).
const dialogIconSx: SxProps<Theme> = (theme) => ({
  margin: "0 auto",
  fontSize: "4rem",
  fill: theme.palette.background.ffGreen,
});

// PARITY RESTORE — v4's `DialogContent` root was `padding: "8px 24px"`, with a
// `&:first-child { paddingTop: 20 }` branch that does NOT apply here (a
// DialogTitle precedes it). v9 changed the root to `padding: "20px 24px"` and
// added `.MuiDialogTitle-root + & { paddingTop: 0 }`, so an uncompensated port
// renders 0 top / 20 bottom instead of 8 / 8.
// The adjacent-sibling rule has to be restated selector-for-selector: its
// specificity (0,2,0) beats a flat `padding` key in `sx` (0,1,0) regardless of
// order. Reading the selector off `dialogTitleClasses` (rather than writing
// `.FfV5DialogTitle-root`) guarantees it stays in step with the class prefix
// and with v9's own rule.
const dialogContentSx: SxProps<Theme> = {
  padding: "8px 24px",
  [`.${dialogTitleClasses.root} + &`]: {
    paddingTop: "8px",
  },
};

// PARITY RESTORE — v4's `DialogContentText` root carried `marginBottom: 12`;
// v9's root style object is empty (`{}`), so every stacked line would close up
// by 12px. Applied to the four texts that relied on the v4 default; the fifth
// (`dialogTextContainerSx`) pinned `marginBottom: 0` in v4 and keeps it.
const dialogContentTextSx: SxProps<Theme> = {
  marginBottom: "12px",
};

// was: useStyles().dialogSubmit
const dialogSubmitSx: SxProps<Theme> = (theme) => ({
  margin: theme.spacing(0, 0, 3, 0),
  width: "100%",
  // PARITY RESTORE — not a v4 rule. v4's Button defaulted to `color="default"`,
  // whose root style was `color: theme.palette.text.primary`. v5+ deleted the
  // `default` colour and made `primary` the default, and this theme's
  // `primary.main` IS the lime that `typography.button` paints the button
  // background with — so the label would render lime-on-lime. Same fix as the
  // T0 Footer pilot.
  color: "text.primary",
});

// was: useStyles().formFieldSet
const formFieldSetSx: SxProps<Theme> = (theme) => ({
  margin: theme.spacing(0, 0, 1, 0),
  "& label": {
    width: "auto",
    fontSize: "26px",
  },
  "& input": {
    padding: "14px 10px",
    fontSize: "1rem",
  },
  // was the separate `phoneInput` rule, applied to the same <input> via
  // `inputClass`. It still carries `!important`, so it wins over
  // react-phone-input-2's own width exactly as it did in v4.
  [`& .${PHONE_INPUT_CLASS}`]: {
    width: "inherit !important",
  },
  "& .react-tel-input .flag-dropdown:before": {
    fontSize: "15px !important",
    top: "-29px !important",
    content: "'' !important",
    color: "#0000008a !important",
    display: "none",
  },
  "& .react-tel-input .form-control": {
    fontSize: "16px !important",
  },
});

// was: useStyles().submitLoader — the `!important`s are load-bearing:
// CircularProgress writes its `size` as an inline `style` on the root, in v9
// exactly as in v4.
const submitLoaderSx: SxProps<Theme> = {
  height: "22px !important",
  width: "22px !important",
  color: "common.white",
};

// was: useStyles().dialogTextContainer
const dialogTextContainerSx: SxProps<Theme> = {
  marginBottom: 0,
  color: "#171717",
};

// was: useStyles().dialogText
const dialogTextSx: SxProps<Theme> = {
  color: "#0000008a",
  textAlign: "center",
  marginBottom: "12px",
};

// NOT PORTED — the v4 `useStyles` object also declared `findOutMoreButton`,
// `submit` and `referralAlert`. No element in this file has ever carried those
// classes, so they emitted CSS that never matched anything. Dropped rather
// than carried forward as unused constants.

const DoorsClosedNotifyMe = (props: {
  open: boolean;
  type: "notify" | "trial";
  closed: () => void;
}) => {
  const [error, setError] = useState<string>();
  const [countryCode, setCountryCode] = useState<string>();
  const [phoneNumber, setPhoneNumber] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const submit = () => {
    if (!countryCode || !phoneNumber) {
      setError("Whoops! You haven't provided a phone number.");
      return;
    }

    setSuccess(false);
    setLoading(true);

    createInterestApplication({
      countryCode,
      phoneNumber,
    })
      .then(() => {
        setSuccess(true);
      })
      .catch(() => {
        setError("Oops! Something went wrong!");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onChange = (value: string, { dialCode }: { dialCode: string }) => {
    setError(undefined);
    setPhoneNumber(value.substring(dialCode.length).replace(/^0+/, ""));
    setCountryCode(dialCode);
  };

  return (
    <Dialog
      maxWidth={"xs"}
      open={props.open}
      onClose={props.closed}
      aria-labelledby="max-width-dialog-title"
    >
      <DialogTitle id="customized-dialog-title" sx={dialogTitleSx}>
        {props.closed ? (
          <IconButton
            aria-label="close"
            sx={closeButtonSx}
            onClick={props.closed}
          >
            <CloseIcon />
          </IconButton>
        ) : null}
      </DialogTitle>
      <DialogContent sx={dialogContentSx}>
        {success && (
          <>
            <DialogContentText
              style={{ textAlign: "center" }}
              sx={dialogContentTextSx}
            >
              <CheckCircleIcon sx={dialogIconSx} />
            </DialogContentText>
            <DialogContentText
              style={{ textAlign: "center" }}
              sx={dialogContentTextSx}
            >
              <b>Your details have been saved</b>
            </DialogContentText>
            <DialogContentText
              style={{ textAlign: "center" }}
              sx={dialogContentTextSx}
            >
              We&apos;ll notify you when the Membership Doors Open.
            </DialogContentText>
          </>
        )}
        {!success && (
          <>
            <DialogContentText
              style={{ textAlign: "center" }}
              sx={dialogContentTextSx}
            >
              <NotificationsActiveIcon sx={dialogIconSx} />
            </DialogContentText>
            <DialogContentText sx={dialogTextContainerSx}>
              {props.type === "trial" ? (
                <Typography variant="body2" sx={dialogTextSx}>
                  We&apos;re not taking on new members at present as we&apos;re
                  focussing on what we do best - out and about capturing great
                  content.
                </Typography>
              ) : (
                ""
              )}
              <Typography
                variant="body1"
                sx={dialogTextSx}
                style={{ marginBottom: "6px" }}
              >
                <b>Notify me when the Doors are Open</b>
              </Typography>
            </DialogContentText>
            {/* v5+ Grid: the `item` prop is gone and the breakpoint props are
                collapsed into `size`. */}
            <Grid container>
              <Grid size={12}>
                <Form onSubmit={submit}>
                  {(props) => (
                    <form onSubmit={props.handleSubmit}>
                      <FormRoot>
                        {error && (
                          <Alert
                            variant="filled"
                            severity="error"
                            style={{ margin: "10px 0" }}
                          >
                            {error}
                          </Alert>
                        )}
                        {/* `spacing={2}` is implemented as `gap: 16px` in v9
                            rather than v4's negative container margin +
                            per-item padding. For this single-column layout the
                            content geometry is identical (16px between rows,
                            no outer offset); the only difference is that the
                            container no longer overhangs its parent by 8px on
                            each side. */}
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12, sm: 12 }} sx={formFieldSetSx}>
                            <PhoneInput
                              inputClass={PHONE_INPUT_CLASS}
                              inputProps={{
                                name: "mobileNumber",
                                required: true,
                              }}
                              enableLongNumbers={true}
                              countryCodeEditable={false}
                              autoFormat={false}
                              enableTerritories={true}
                              country="gb"
                              placeholder="Mobile Number"
                              aria-label="Mobile Number"
                              preferredCountries={[
                                "gb",
                                "ie",
                                "us",
                                "au",
                                "nz",
                              ]}
                              excludeCountries={["je"]}
                              onChange={onChange}
                              disabled={loading}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 12 }}>
                            <Button
                              type="submit"
                              size="large"
                              fullWidth
                              sx={dialogSubmitSx}
                              disabled={loading}
                            >
                              {!loading && <>Submit</>}
                              {loading && (
                                <>
                                  <CircularProgress sx={submitLoaderSx} />
                                </>
                              )}
                            </Button>
                          </Grid>
                        </Grid>
                      </FormRoot>
                    </form>
                  )}
                </Form>
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DoorsClosedNotifyMe;
